import * as THREE from 'three'
import { type DebugMaterialRegistry, TYPE_COLORS } from './debug-materials'
import { cycleMeshSwatch } from '../level/textures'

type RegisteredEntry = import('./debug-materials').RegisteredEntry

/**
 * Resolves the canonical ID string for a registry entry, falling back to the
 * underlying mesh's name, then to a synthesized `${type}_${meshId}` token.
 * Shared by the HUD and JSON-copy paths so the fallback chain lives in one place.
 */
function resolveEntryId(entry: RegisteredEntry, hitMesh: THREE.Mesh): string {
  return entry.meta?.id ?? hitMesh.name ?? `${entry.type}_${hitMesh.id}`
}

function roundVec3(vec: THREE.Vector3, decimals: number): { x: number; y: number; z: number } {
  return {
    x: Number(vec.x.toFixed(decimals)),
    y: Number(vec.y.toFixed(decimals)),
    z: Number(vec.z.toFixed(decimals)),
  }
}

function roundDimensions(
  dims: [number, number, number],
  decimals: number,
): { width: number; height: number; depth: number } {
  return {
    width: Number(dims[0].toFixed(decimals)),
    height: Number(dims[1].toFixed(decimals)),
    depth: Number(dims[2].toFixed(decimals)),
  }
}

export class WthitInspector {
  private camera: THREE.Camera
  private readonly registry: DebugMaterialRegistry
  private readonly raycaster = new THREE.Raycaster()
  private readonly container?: HTMLDivElement

  private titleEl?: HTMLSpanElement
  private badgeEl?: HTMLSpanElement
  private sourceEl?: HTMLDivElement
  private metricsEl?: HTMLDivElement
  private detailsEl?: HTMLDivElement

  private isVisible = false

  constructor(_scene: THREE.Scene, camera: THREE.Camera, registry: DebugMaterialRegistry) {
    this.camera = camera
    this.registry = registry

    if (typeof document !== 'undefined') {
      this.container = document.createElement('div')
      this.container.id = 'wthit-inspector'
      this.container.style.position = 'fixed'
      this.container.style.top = '16px'
      this.container.style.left = '50%'
      this.container.style.transform = 'translateX(-50%)'
      this.container.style.minWidth = '340px'
      this.container.style.maxWidth = '520px'
      this.container.style.backgroundColor = 'rgba(8, 12, 18, 0.92)'
      this.container.style.border = '1px solid rgba(0, 240, 255, 0.6)'
      this.container.style.borderRadius = '6px'
      this.container.style.padding = '8px 14px'
      this.container.style.fontFamily = "'Consolas', 'Courier New', monospace"
      this.container.style.fontSize = '12px'
      this.container.style.color = '#e2e8f0'
      this.container.style.zIndex = '9999'
      this.container.style.pointerEvents = 'none'
      this.container.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.75), 0 0 10px rgba(0, 240, 255, 0.2)'
      this.container.style.transition = 'opacity 0.15s ease-in-out, transform 0.15s ease-in-out'
      this.container.style.display = 'none'

      this.container.innerHTML = `
        <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 4px; gap: 8px;">
          <div style="display: flex; align-items: center; gap: 6px; overflow: hidden;">
            <span id="wthit-badge" style="display: inline-block; padding: 2px 6px; border-radius: 3px; font-size: 10px; font-weight: bold; text-transform: uppercase; background: #ff007f; color: #fff;">DOOR</span>
            <span id="wthit-title" style="font-weight: bold; color: #ffffff; white-space: nowrap; text-overflow: ellipsis; overflow: hidden;">Target Range Door</span>
          </div>
          <span style="font-size: 10px; color: #00f0ff; letter-spacing: 0.5px; opacity: 0.8;">WTHIT // INSPECTOR</span>
        </div>
        <div id="wthit-source" style="color: #94a3b8; font-size: 11px; margin-bottom: 4px; word-break: break-all;">
          ESOTERIA/AREAS.E3 : CSlidingDoor piece: targetdr
        </div>
        <div id="wthit-metrics" style="display: flex; justify-content: space-between; color: #38bdf8; font-size: 11px; border-top: 1px solid rgba(255,255,255,0.1); padding-top: 4px;">
          <span>Pos: (-2.07, 0.00, 4.54)</span>
          <span>Dist: 3.1m</span>
        </div>
        <div id="wthit-details" style="color: #a7f3d0; font-size: 11px; margin-top: 3px; display: none;"></div>
      `

      document.body.appendChild(this.container)
      this.badgeEl = this.container.querySelector('#wthit-badge')!
      this.titleEl = this.container.querySelector('#wthit-title')!
      this.sourceEl = this.container.querySelector('#wthit-source')!
      this.metricsEl = this.container.querySelector('#wthit-metrics')!
      this.detailsEl = this.container.querySelector('#wthit-details')!
    }
  }

  private findRegisteredHit(hits: THREE.Intersection[]): { hit: THREE.Intersection; mesh: THREE.Mesh; entry: RegisteredEntry } | null {
    for (const hit of hits) {
      let current: THREE.Object3D | null = hit.object
      while (current) {
        if (current instanceof THREE.Mesh) {
          const entry = this.registry.getEntryByMesh(current)
          if (entry) {
            return { hit, mesh: current, entry }
          }
        }
        current = current.parent
      }
    }
    return null
  }

  /**
   * Single source of truth for the crosshair raycast: optionally swap camera,
   * update world matrices, raycast from NDC (0,0), find the first registered
   * hit, and reject anything beyond `maxDistance`. Used by update(),
   * getCurrentTargetData(), and cycleTargetSwatch() -- eliminating the
   * 5-line raycast + 4-line guard triple-duplication flagged by jscpd.
   */
  private acquireTarget(
    camera: THREE.Camera | undefined,
    maxDistance: number,
  ): { hit: THREE.Intersection; mesh: THREE.Mesh; entry: RegisteredEntry } | null {
    if (camera) this.camera = camera
    this.camera.updateMatrixWorld(true)
    this.raycaster.setFromCamera(new THREE.Vector2(0, 0), this.camera)
    const registeredMeshes = this.registry.getAllMeshes()
    const hits = this.raycaster.intersectObjects(registeredMeshes, true)

    const target = this.findRegisteredHit(hits)
    if (!target || target.hit.distance > maxDistance) return null
    return target
  }

  update(camera?: THREE.Camera): void {
    if (!this.container || !this.badgeEl || !this.titleEl || !this.sourceEl || !this.metricsEl || !this.detailsEl) {
      return
    }

    const target = this.acquireTarget(camera, 40)
    if (!target) {
      if (this.isVisible) {
        this.container.style.display = 'none'
        this.isVisible = false
      }
      return
    }

    const { hit, mesh: hitMesh, entry } = target

    const typeInfo = TYPE_COLORS[entry.type]
    const hexColor = `#${typeInfo.color.toString(16).padStart(6, '0')}`

    this.badgeEl.textContent = typeInfo.label
    this.badgeEl.style.backgroundColor = hexColor
    this.badgeEl.style.color = entry.type === 'elevator' || entry.type === 'trim' ? '#000000' : '#ffffff'

    const idText = resolveEntryId(entry, hitMesh)
    const nameText = entry.meta?.name ? `${entry.meta.name} (${idText})` : idText
    this.titleEl.textContent = nameText

    const sourceText = entry.meta?.sourceTag
      ? `Source: ${entry.meta.sourceTag}`
      : `Object Class: ${entry.type}`
    this.sourceEl.textContent = sourceText

    const pos = hitMesh.position
    const dist = hit.distance.toFixed(2)
    const dims = entry.meta?.dimensions
      ? ` | Size: ${entry.meta.dimensions[0].toFixed(2)}×${entry.meta.dimensions[1].toFixed(2)}×${entry.meta.dimensions[2].toFixed(2)}m`
      : ''

    this.metricsEl.innerHTML = `<span>Pos: (${pos.x.toFixed(2)}, ${pos.y.toFixed(2)}, ${pos.z.toFixed(2)})${dims}</span><span>Dist: ${dist}m</span>`

    if (entry.meta?.getDetails) {
      const details = entry.meta.getDetails()
      const detailKeys = Object.keys(details)
      if (detailKeys.length > 0) {
        const detailStr = detailKeys
          .map((k) => `<span style="color: #94a3b8;">${k}:</span> <b style="color: #f1f5f9;">${details[k]}</b>`)
          .join(' | ')
        this.detailsEl.innerHTML = detailStr
        this.detailsEl.style.display = 'block'
      } else {
        this.detailsEl.style.display = 'none'
      }
    } else {
      this.detailsEl.style.display = 'none'
    }

    if (!this.isVisible) {
      this.container.style.display = 'block'
      this.isVisible = true
    }
  }

  getCurrentTargetData(camera?: THREE.Camera): Record<string, any> | null {
    const target = this.acquireTarget(camera, 50)
    if (!target) return null

    const { hit, mesh: hitMesh, entry } = target
    const pos = hitMesh.position
    const id = resolveEntryId(entry, hitMesh)
    const name = entry.meta?.name ?? id
    const details = entry.meta?.getDetails ? entry.meta.getDetails() : {}

    return {
      id,
      name,
      type: entry.type,
      sourceTag: entry.meta?.sourceTag ?? null,
      distanceMeters: Number(hit.distance.toFixed(3)),
      position: roundVec3(pos, 3),
      dimensionsMeters: entry.meta?.dimensions ? roundDimensions(entry.meta.dimensions, 3) : null,
      details,
      gltfAsset: (details as any)['GLTF Asset'] ?? null,
      timestamp: new Date().toISOString(),
    }
  }

  /**
   * Cycles through authentic sector texture swatches on the currently targeted object.
   * Fully encapsulates raycasting, material caching, registry synchronization, and HUD refresh.
   */
  cycleTargetSwatch(step = 1, camera?: THREE.Camera): { texture: string; index: number; total: number; state?: string } | null {
    const target = this.acquireTarget(camera, 50)
    if (!target) return null

    if (target.mesh.userData?.cycleState) {
      const stateResult = target.mesh.userData.cycleState()
      this.update()
      return stateResult
    }

    const result = cycleMeshSwatch(target.mesh, target.entry.type, this.registry, step)
    this.update()
    return result
  }

  destroy(): void {
    if (this.container && this.container.parentElement) {
      this.container.parentElement.removeChild(this.container)
    }
  }
}